import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from 'react-hot-toast';
import MaiAIChat from './components/MaiAIChat';
import ScrollToTop from './components/ScrollToTop';

// Import all your page components
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import DashboardPage from "./pages/DashboardPage";
import PeoplePage from "./pages/PeoplePage";
import LocationPage from "./pages/LocationPage";
import SchedulePage from "./pages/SchedulePage";
import NewsFeedPage from "./pages/NewsFeedPage";
import MePage from "./pages/MePage";
import AcceptInvitePage from './pages/AcceptInvitePage';
// import ReportPage from './pages/ReportPage';
import ProfilePage from "../src/pages/ProfilePage";
import AdminProfilePage from "../src/pages/AdminProfilePage ";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import OpenSchedulePage from "./pages/OpenSchedulePage";
import PricingComponent from "./pages/PricingComponent";
import Solutions from './pages/Solutions';
import Contactpage from './pages/Contactpage';
import TermsandConditions from './pages/TermsandConditions';
import Privacypolicy from './pages/Privacypolicy';
import BlogPage from './pages/BlogPage';
import DocumentationPage from './pages/DocumentationPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import BusinessProfile from "./pages/BusinessProfile";
import SuperAdminPage from "./pages/SuperAdminPage";
import ComplaintsPage from "./pages/ComplaintsPage";
import ShiftSchedulingComponent from "./pages/ShiftSchedulingComponent";
import HrComponent from "./pages/HrComponent";
import PredictiveAnalyticsComponent from "./pages/PredictiveAnalyticsComponent";
import IntegrationPage from "./pages/IntegrationPage";
import HelpAndSettings from "./pages/HelpAndSettings";
import SettingsPage from "./pages/SettingsPage";
import AdminDashboardOverview from "./pages/AdminDashboardOverview";
import TimesheetManagement from "./pages/TimesheetManagement";
import AnalyticsDashboard from "./pages/AnalyticsDashboard";
import TasksPage from "./pages/TasksPage";
import ScheduleFeature from "./pages/ScheduleFeature";
import TimesheetFeature from "./pages/TimesheetFeature";
import AnalyticsFeature from "./pages/AnalyticsFeature";
import AdminFeature from "./pages/AdminFeature";
import CommunicationFeature from "./pages/CommunicationFeature";
import AIFeature from "./pages/AIFeature";

// Import Industry Pages
import HealthcareIndustry from "./pages/industries/HealthcareIndustry";
import HospitalityIndustry from "./pages/industries/HospitalityIndustry";
import HotelsIndustry from "./pages/industries/HotelsIndustry";
import CallCentersIndustry from "./pages/industries/CallCentersIndustry";
import RetailIndustry from "./pages/industries/RetailIndustry";
import ManufacturingIndustry from "./pages/industries/ManufacturingIndustry";

const ProtectedRoute = ({ children }) => {
  const { isLoggedIn, isLoading } = useAuth();

  if (isLoading) {
    return null; // You could also show a loading spinner here
  }

  if (!isLoggedIn) {
    return <Navigate to="/login" replace />;
  }

  return children;
};
const RoleBasedProfile = () => {
  const { user } = useAuth();
  return user?.role === 'admin' ? <AdminProfilePage/> : <ProfilePage />;
};


function App() {
  return (
    <>
    <Toaster position="top-right" reverseOrder={false} />
    <Router>
      <ScrollToTop />
      <AuthProvider>
        <Routes>
          <Route path="/" element={<LandingPage />} />

          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/invite/:token" element={<AcceptInvitePage />} />
          <Route path='/forgot-password' element={<ForgotPasswordPage/>}/>
          <Route path="/reset-password/:resettoken" element={<ResetPasswordPage />} />
          <Route path="/pricing" element={<PricingComponent />} />
          <Route path="/solutions" element={<Solutions/>}/>
          <Route path="/contact" element={<Contactpage/>}/>
          <Route path="/tandc" element={<TermsandConditions/>}/>
          <Route path="/privacypolicy" element={<Privacypolicy/>}/>
          <Route path="/blog" element={<BlogPage/>}/>
          <Route path="/documentation" element={<DocumentationPage/>}/>
          <Route path="/features/scheduling" element={<ShiftSchedulingComponent/>}/>
          <Route path="/features/hr" element={<HrComponent/>}/>
          <Route path="/features/predictive-analytics" element={<PredictiveAnalyticsComponent/>}/>
          
          {/* Feature Detail Pages */}
          <Route path="/schedule-feature" element={<ScheduleFeature />} />
          <Route path="/timesheet-feature" element={<TimesheetFeature />} />
          <Route path="/analytics-feature" element={<AnalyticsFeature />} />
          <Route path="/admin-feature" element={<AdminFeature />} />
          <Route path="/communication-feature" element={<CommunicationFeature />} />
          <Route path="/ai-feature" element={<AIFeature />} />
          
          {/* Industry Detail Pages */}
          <Route path="/industries/healthcare" element={<HealthcareIndustry />} />
          <Route path="/industries/hospitality" element={<HospitalityIndustry />} />
          <Route path="/industries/hotels" element={<HotelsIndustry />} />
          <Route path="/industries/call-centers" element={<CallCentersIndustry />} />
          <Route path="/industries/retail" element={<RetailIndustry />} />
          <Route path="/industries/manufacturing" element={<ManufacturingIndustry />} />


          <Route
            path="/" 
            element={
              <ProtectedRoute>
                <DashboardPage /> 
              </ProtectedRoute>
            }
          >
            {/* Nested protected routes that appear within the DashboardPage layout */}
            <Route path="dashboard" element={<AdminDashboardOverview />} />
            <Route path="people" element={<PeoplePage />} />
             <Route path="profile" element={ <ProtectedRoute> <RoleBasedProfile /> </ProtectedRoute> } />
            <Route path="locations" element={<LocationPage />} />
            <Route path="schedule" element={<SchedulePage />} />
            <Route path="openschedules" element={<OpenSchedulePage/>}/>
            <Route path="timesheets" element={<TimesheetManagement/>}/>
            <Route path="analytics" element={<AnalyticsDashboard/>}/>
            <Route path="tasks" element={<TasksPage />} />
            <Route path="certifications" element={<MePage />} /> {/* TODO: Create CertificationsPage */}
            <Route path="newsfeed" element={<NewsFeedPage />} />
            {/* <Route path="reports" element={<ReportPage />} /> */}
            <Route path="businessprofile" element={<BusinessProfile/>}/>
            <Route path="/superadmin" element={<SuperAdminPage/>}/>
            <Route path="compliance" element = {<ComplaintsPage/>}/>
            <Route path="integrations" element={<IntegrationPage/>}/>
            <Route path="helpandsettings" element={<HelpAndSettings/>}/>
            <Route path="settings" element={<SettingsPage/>}/>
          </Route>
          <Route path="*" element={<div>404 Not Found</div>} />
          <Route path='LandingPage' element></Route>
        </Routes>
        {/* Mai AI Assistant - Available on all pages */}
        <MaiAIChat />
      </AuthProvider>
    </Router>
    </>
  );
}

export default App;